/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  useHooks,
  breakpoints,
} from '@everlywell/leaves';
import { GatsbyImage as Img, IGatsbyImageData } from 'gatsby-plugin-image';
import Cookies from 'js-cookie';
import React, { useState, useContext } from 'react';
import ewAnalytics from 'utils/analytics';
import { addToCart, addToCartDataLayer } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { CookieNames } from 'utils/constants/cookies';
import { useMembershipProducts } from 'utils/hooks';
import { UserContext } from 'utils/hooks/useUserContext/context';
import { CTA, ProductsBySlugType } from 'utils/types';
import { v4 as uuidv4 } from 'uuid';

import snowflakes from '../../../images/themes/Winter2020/snowflakes.svg';
import { AllProductPromosContext } from '../../../templates/homepage';
import { Themable, Theme } from '../../Themable';
import lines from './lines.svg';
import * as S from './styles';

declare global {
  interface Window {
    dataLayer: any;
  }
}

type ProductCardType = {
  heading: string;
  subheading: string;
  image: IGatsbyImageData;
  number: number;
  slug: string;
  showRating: boolean;
  description: string;
  reviewData: {
    numOfReviews: number;
    rating: number;
  };
};

export interface PopularTestsSectionProps {
  headline: string;
  mainCta: CTA;
  listOfContent: ProductCardType[];
  productsBySlug: ProductsBySlugType;
}

const getPriceForMembershipProduct = (price?: string, memberPrice?: string) =>
  memberPrice && price ? `$${(+price).toFixed(0)}` : ' ';

export const PopularTestsSection: React.FC<PopularTestsSectionProps> = ({
  headline,
  mainCta,
  listOfContent,
  productsBySlug,
}) => {
  const [itemsInCart, setItemsInCart] = useState<Array<string>>([]);
  const windowSize = useHooks.useWindowSize();
  const tabletUp = windowSize?.width
    ? windowSize.width >= breakpoints.forTabletHorizontalUp
    : false;
  const { userData } = useContext(UserContext);
  const allProductPromoData = useContext(AllProductPromosContext);
  const experimentCookie = Cookies.get(CookieNames.HomepageExperiment);

  const productsWithMembership = useMembershipProducts(
    userData,
    productsBySlug,
  );

  const handleClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.HOMEPAGE_POPULAR_ALL_TESTS_CTA,
      },
    });
  };

  const addToCartGTM = (slug: string, eventId: string) => {
    if (!window || !window.dataLayer) {
      return;
    }

    const productData = productsWithMembership[slug] || {};

    if (
      !productData ||
      !productData.productId ||
      !productData.name ||
      !productData.price
    )
      return;

    addToCartDataLayer(
      // @ts-ignore
      productData.productId,
      productData.name,
      productData.price,
      eventId,
    );
  };

  const handleAddToCartClick = (slug: string) => {
    setItemsInCart([...itemsInCart, slug]);

    // Remove 'added' UI state
    setTimeout(
      () => setItemsInCart(itemsInCart.filter((x) => x !== slug)),
      1200,
    );

    const product = productsWithMembership[slug];
    const eventId = uuidv4();

    addToCart(product.variantId, undefined, undefined, undefined, eventId);
    addToCartGTM(slug, eventId);

    ewAnalytics.track({
      data: {
        label: ANALYTICS.LABELS.ADD_TO_CART,
        'Test ID': product.productId,
        'Test Name': product.name,
        Experiment: experimentCookie ? experimentCookie : '',
      },
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
    });
  };

  const handleCardClick = (slug: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.HOMEPAGE_POPULAR_PRODUCT_CARD,
        slug,
      },
    });
  };
  return (
    <S.Section data-testid="popularTests">
      <S.InnerContent>
        <Themable>
          <S.Img theme={Theme.Default} aria-hidden="true" src={lines} alt="" />
          <S.Img
            theme={Theme.Winter2020}
            aria-hidden="true"
            src={snowflakes}
            style={{
              marginLeft: '-24px',
              bottom: `-8px`,
            }}
            alt=""
          />
        </Themable>
        <Container>
          <Row>
            <S.MobileCardsCol xs={12}>
              <S.ContentContainer>
                <S.DesktopHeadline>{headline}</S.DesktopHeadline>
                <S.DesktopShopCTAButton
                  appearance="secondary"
                  href={mainCta.url}
                  onClick={handleClick}
                >
                  {mainCta.text}
                </S.DesktopShopCTAButton>
                <Themable>
                  <S.DesktopImg
                    theme={Theme.Default}
                    aria-hidden="true"
                    src={lines}
                    alt=""
                  />
                  <S.DesktopImg
                    theme={Theme.Winter2020}
                    aria-hidden="true"
                    src={snowflakes}
                    alt=""
                  />
                </Themable>
              </S.ContentContainer>
              <S.DesktopCardsContainer>
                {listOfContent.map((item, i) => {
                  const productPromoData = allProductPromoData?.find(
                    (page) => page.slug === item.slug,
                  );
                  return (
                    <S.CardWrapper
                      key={`desktop-card-${i}`}
                      onClick={() => handleCardClick(item.slug)}
                    >
                      <Card.Product
                        image={<Img image={item.image} alt={item.heading} />}
                        imageAlt={`${item.heading} box image`}
                        productName={item.heading}
                        productUrl={
                          productsWithMembership[item.slug] &&
                          `/products/${productsWithMembership[item.slug].slug}/`
                        }
                        price={getPriceForMembershipProduct(
                          productsWithMembership[item.slug]?.price,
                          productsWithMembership[item.slug]?.memberPrice,
                        )}
                        isOutOfStock={false} // We don't have this data yet, so we set it to false
                        reviewData={
                          // get the review data from the identifier
                          item.showRating ? item.reviewData : null
                        }
                        // @ts-ignore
                        promoPrice={
                          productsWithMembership[item.slug]?.memberPrice || ''
                        }
                        promoText={
                          productsWithMembership[item.slug]?.promoText ||
                          productPromoData?.promoText
                        }
                        showAddToCart={false}
                        onAddToCartClick={() => handleAddToCartClick(item.slug)}
                        addedToCart={itemsInCart.includes(item.slug)}
                        onRemoveFromCartClick={() => null}
                        removedFromCart={!itemsInCart.includes(item.slug)}
                        description={tabletUp ? item.description : ''}
                        showReviewCount={false}
                        isOnSale={productPromoData?.onSale}
                      />
                    </S.CardWrapper>
                  );
                })}
              </S.DesktopCardsContainer>
            </S.MobileCardsCol>
          </Row>
        </Container>
        <S.MobileShopCTAContainer>
          <Row center="xs">
            <Col xs={12}>
              <Button
                appearance="primary"
                href={mainCta.url}
                onClick={handleClick}
              >
                {mainCta.text}
              </Button>
            </Col>
          </Row>
        </S.MobileShopCTAContainer>
      </S.InnerContent>
    </S.Section>
  );
};

export default PopularTestsSection;
